import { type QuestionWithScoreData, useCandidateSubmissions } from '@jobilla/nexus-sdk'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { logger, TimeAgo } from '@/ui'

import { ApplicationResponseItem, type ScoredOption } from './application-response-item'
import { ScoreBadge } from './score-badge'

type Props = {
	candidateId: string
	formQuestions?: QuestionWithScoreData[]
}

export function ApplicationHistory({ candidateId, formQuestions }: Props) {
	const { t } = useTranslation('common')
	const { data, isLoading, isError, error } = useCandidateSubmissions(candidateId)

	if (isLoading) {
		return <p className="text-sm text-gray-400 text-center py-8">{t('candidateCard.history.loading')}</p>
	}

	if (isError) {
		logger.error(error ?? new Error('Failed to load candidate submissions'), {
			tags: { component: 'ApplicationHistory' },
			data: { candidateId },
		})
		return <p className="text-sm text-red-600 text-center py-8">{t('candidateCard.history.error')}</p>
	}

	const submissions = data?.data ?? []

	if (submissions.length <= 1) {
		return <p className="text-sm text-gray-400 text-center py-8">{t('candidateCard.history.empty')}</p>
	}

	return (
		<div className="space-y-4">
			{submissions.map((submission, index) => {
				const scoreBreakdown = submission.score?.breakdown ?? []
				const isDisqualified = submission.questions.some((question) =>
					question.options?.some((responseOption) => {
						const isSelected = question.answers.some(answer =>
							answer.answer === responseOption.label || answer.optionId === responseOption.id
						)
						if (!isSelected) { return false }
						const matchingFormOption = formQuestions
							?.flatMap(formQuestion => formQuestion.options ?? [])
							.find(formOption => formOption.id === responseOption.id)
						return matchingFormOption?.isDisqualifying === true
					})
				)

				const isLatest = index === 0

				return (
					<details
						key={submission.id}
						open={!isLatest}
						className="group border border-gray-200 rounded-lg overflow-hidden"
					>
						<summary className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
							<div className="flex items-center gap-2 text-sm text-gray-700">
								<ChevronDownIcon className="size-4 text-gray-400 transition-transform group-open:rotate-180" />
								<span className="font-medium">{t('candidateCard.history.submittedAt')}</span>
								<TimeAgo date={new Date(submission.createdAt)} />
								{isLatest && (
									<span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
										{t('candidateCard.history.latestLabel')}
									</span>
								)}
							</div>
							{submission.score && (
								<ScoreBadge percentage={submission.score.percentage} isDisqualified={isDisqualified} />
							)}
						</summary>
						<div className="px-4 py-3 divide-y divide-gray-100">
							{submission.questions.map((question, qIndex) => {
								const matchingFormQuestion = formQuestions?.find(formQuestion =>
									question.options?.some(responseOption =>
										formQuestion.options?.some(formOption => formOption.id === responseOption.id)
									)
								)
								const scoreContribution = matchingFormQuestion?.id
									? scoreBreakdown.find(entry => entry.questionId === matchingFormQuestion.id)
									: undefined
								const scoredOptions: ScoredOption[] | undefined = question.options?.map(
									responseOption => {
										const formOption = matchingFormQuestion?.options?.find(option =>
											option.id === responseOption.id
										)
										return {
											...responseOption,
											scoreValue: formOption?.scoreValue ?? null,
											isDisqualifying: formOption?.isDisqualifying ?? null,
										}
									},
								)

								return (
									<ApplicationResponseItem
										key={qIndex}
										questionNumber={qIndex + 1}
										question={question.question}
										answer={question.answers}
										options={scoredOptions}
										scoreContribution={scoreContribution}
									/>
								)
							})}
						</div>
					</details>
				)
			})}
		</div>
	)
}
